import type { Category } from '../categories/categoryTree'
import { PAGE_SIZE, writeFilters, type TxnQuery } from '../transactions/filters'
import type { Envelope } from './budgetWire'

/**
 * The transactions behind an envelope: `GET /transactions?budget_id=`, which lists exactly the rows the
 * server sums into `progress.spent` (backend #529) — its period, its category and children (all spending
 * when it has none), posted expenses only. Nothing else is sent: the server owns the period and the scope,
 * and `budget_id` is not combinable with `cashflow_kind`. The rows are never summed here.
 */
export function envelopeTxnQuery(envelope: Envelope, page: number): TxnQuery {
  return { budget_id: envelope.id, page, page_size: PAGE_SIZE }
}

/**
 * "View in Transactions" (P5): only where /transactions' URL filters can express roughly the same rows, which
 * is a MONTHLY envelope on a LEAF category or on no category. The link is a superset of the list beside it:
 * the list is the server's budget set, which leaves out transactions excluded from budgets
 * (`is_excluded`), while /transactions has no such filter and still shows them. A parent category (the screen has no group
 * filter), a weekly or yearly window, or categories not yet loaded (cannot prove a leaf) give `null`.
 */
export function envelopeTransactionsHref(
  envelope: Envelope,
  categories: readonly Category[] | null,
  today: Date,
): string | null {
  if (envelope.period !== 'monthly' || categories === null) return null
  if (envelope.categoryId !== null) {
    if (!categories.some((c) => c.id === envelope.categoryId)) return null
    if (categories.some((c) => c.parent_id === envelope.categoryId)) return null
  }
  const params = writeFilters(
    {
      month: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`,
      q: '',
      accountId: null,
      categoryId: envelope.categoryId,
      ownerId: null,
      tag: null,
      spentMin: 1,
      spentMax: null,
      pending: false,
      review: false,
      page: 1,
    },
    today,
  )
  return `/transactions?${params}`
}
